<template>
  <div :class="['max-w-600 rounded-md field-wrapper', isScrollInto ? 'bg-red-300' : '']">
    <div class="label">
      <img src="../assets/choice.svg" alt="选择题" />
      <span class="px-2">{{ inputType === 'radio' ? '单选题' : '多选题' }}</span>
    </div>
    <div class="mb-4">
      <h1 class="title-text text-lg font-normal whitespace-pre-wrap">
        <span>{{ data.title.trim() }}</span>
        <span v-if="totalScore">({{ totalScore }}分)</span>
      </h1>
    </div>

    <form :class="['content', warningClass]">
      <!-- radio -->
      <fieldset :disabled="isSubmit">
        <ul v-if="inputType === 'radio'">
          <li v-for="(option, idx) in data.options" :key="idx" class="mb-4">
            <!-- Object -->
            <input
              :id="`${option.content}_${idx}_${_uid}`"
              v-model="picked"
              :class="[
                'hidden',
                getChecked([(userAnswer && userAnswer.picked) || ''], option.mark)
              ]"
              type="radio"
              :value="option.mark"
            />
            <label :for="`${option.content}_${idx}_${_uid}`">
              <span>{{ `${option.mark}. ${option.content}` }}</span>
            </label>
          </li>
        </ul>
      </fieldset>

      <!-- checkbox -->
      <fieldset :disabled="isSubmit">
        <ul v-if="inputType === 'checkbox'">
          <li v-for="(option, idx) in data.options" :key="idx" class="mb-4">
            <input
              :id="`${option.content}_${idx}_${_uid}`"
              v-model="checkedNames"
              :class="[
                'hidden',
                getChecked((userAnswer && userAnswer.checkedNames) || [], option.mark)
              ]"
              type="checkbox"
              :value="option.mark"
            />
            <label :for="`${option.content}_${idx}_${_uid}`">
              <span>{{ `${option.mark}. ${option.content}` }}</span>
            </label>
          </li>
        </ul>
      </fieldset>

      <transition name="fade" mode="out-in">
        <div
          v-if="isSubmit && (data.answer || data.analysis)"
          class="answer px-4 py-2.5 rounded-md font-medium border border-gray-300 bg-white"
        >
          <div class="answer-label">解析</div>
          <hr class="my-3" />
          <div class="answer">
            <span>正确答案:</span>
            <span v-for="answer in data.answer" :key="answer" class="ml-2">{{ answer }}</span>
          </div>
          <div class="analysis">
            {{ data.analysis }}
          </div>
        </div>
      </transition>
    </form>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { baseSurvey, SurveySaveData } from '../baseSurvey';

export type SurveyOptions = Array<SurveyOption>;
export type SurveyOption = {
  mark: string;
  content: string;
  score: OptionScore;
};
export type OptionScore = OptionScoreSingle | OptionScoreMultiple;
export type OptionScoreSingle = number;
export type OptionScoreMultiple = Array<{ type: string; value: number }>;
export type ChoicType = 'Radio' | 'Checkbox' | '单选' | '多选';

export type UserAnswer = {
  picked: string;
  pickedContent: Array<string>;
  checkedNames: Array<string>;
  checkedNameContent: Array<string>;
};

@Component({
  name: 'Choice'
})
export default class Choice extends Vue implements baseSurvey {
  @Prop({ type: Object }) data: {
    $id: string;
    $kind: ChoicType;
    title: string;
    options: SurveyOptions;
    answer?: Array<string>;
    analysis: string;
    reviewMark?: string;
    maxSelect: number;
    minSelect: number;
  };
  @Prop({ type: Boolean }) isSubmit: boolean;
  @Prop({ type: Number }) index: number;

  userAnswer: UserAnswer | null = null;

  // out
  picked = '';
  checkedNames: Array<string> = [];

  isScrollInto = false;

  get inputType() {
    const typeMap = {
      Radio: 'radio',
      Checkbox: 'checkbox',
      单选: 'radio',
      多选: 'checkbox'
    };
    return typeMap[this.data.$kind];
  }

  get isRight() {
    // 不存在答案
    if (!this.data.answer || !this.isSubmit) {
      return true;
    }

    if (this.inputType === 'radio') {
      return this.data.answer.includes(this.userAnswer?.picked || '');
    }

    if (this.inputType === 'checkbox') {
      return (
        this.data.answer.filter((v) => this.userAnswer?.checkedNames.includes(v)).length ===
          this.data.answer.length &&
        this.userAnswer?.checkedNames.length === this.data.answer.length
      );
    }
  }

  get warningClass() {
    if (!this.isSubmit) return 'blank';
    if (this.isRight) {
      return 'success';
    } else {
      return 'warning';
    }
  }

  get totalScore() {
    return this.data.options.reduce((total: number, option) => {
      if (typeof option === 'string') return 0;
      if (typeof option.score === 'object') {
        return 0;
      }
      return total + (option.score || 0);
    }, 0);
  }

  @Watch('picked')
  @Watch('checkedNames')
  checkContent() {
    this.$emit('fieldChange');
  }

  @Watch('checkedNames')
  maxLength() {
    if (!this.data.maxSelect) return;
    this.$nextTick(() => {
      if (this.checkedNames.length > this.data.maxSelect) {
        this.checkedNames.pop();
      }
    });
  }

  mounted() {
    this.$emit('addField', [this]);
  }

  updateUserAnswer(data: SurveySaveData) {
    if (!data) return;
    this.userAnswer = data.content as UserAnswer;
    this.picked = this.userAnswer.picked;
    this.checkedNames = this.userAnswer.checkedNames;
  }

  getChecked(input: Array<string>, item: string) {
    if (!input) return '';
    return input.includes(item) ? 'checked' : '';
  }

  checkEmpty() {
    if (this.inputType === 'checkbox' && this.data.minSelect > this.checkedNames.length) {
      return true;
    }
    if (!this.picked && !this.checkedNames.length) return true;
    return false;
  }

  followField() {
    this.$el.scrollIntoView({ behavior: 'smooth' });
    this.isScrollInto = true;
    setTimeout(() => {
      this.isScrollInto = false;
    }, 700);
  }

  getValue() {
    return {
      index: this.index,
      type: 'choice',
      title: this.data.title,
      content: {
        picked: this.picked,
        pickedContent: this.data.options
          .filter((item) => {
            if (typeof item === 'string') {
              return item === this.picked;
            }
            return item.mark === this.picked;
          })
          .map((item) => {
            if (typeof item === 'string') {
              return item;
            }
            return item.content;
          }),
        checkedNames: this.checkedNames,
        checkedNameContent: this.data.options
          .filter((item) => {
            if (typeof item === 'string') {
              return this.checkedNames.includes(item);
            }
            return this.checkedNames.includes(item.mark);
          })
          .map((item) => {
            if (typeof item === 'string') {
              return item;
            }
            return item.content;
          })
      }
    };
  }

  getMark() {
    return this.data.reviewMark;
  }
}
</script>

<style scoped>
.field-wrapper {
  transition: all 1s;
}
.title-text {
  color: #1a051d;
}
.label {
  @apply mb-4 flex text-motif-500;
}

.content input:checked + label,
.content .checked + label {
  @apply text-motif-500 border-motif-500;
  background-color: rgba(64, 71, 245, 0.05);
}

.content.warning input:checked + label,
.content.warning .checked + label {
  color: #666666;
  background-color: rgba(255, 0, 0, 0.1);
  border-color: #ed6559;
}

.content.success input:checked + label,
.content.success .checked + label {
  color: #666666;
  background: rgba(94, 205, 139, 0.06);
  border-color: #5ecd8b;
}

.content label {
  @apply block px-4 py-2.5 rounded-md cursor-pointer font-normal;
  color: #666666;
  border: 1px solid #d0d0d6;
}

@screen md {
  .blank label:hover {
    @apply bg-base;
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
